/** Category create/edit form с tabs (general / icon).
 *
 * «Основное» объединяет название/описание RU, переводы uz/en и параметры
 * (родитель, slug, сортировка, видимость) — раньше «Переводы» была отдельной
 * вкладкой, порядок был не очевиден. Тот же порядок, что и в products/edit.tsx.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { CategoryIconUploader } from "@/components/categories/CategoryIconUploader";
import { StickySaveBar } from "@/components/forms/StickySaveBar";
import { TranslateButton } from "@/components/forms/TranslateButton";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { ParentCategoryPicker } from "@/components/products/ParentCategoryPicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const langs = ["ru", "uz", "en"] as const;

// Только русский требуется; uz и en — опциональные.
// Backend reject'нет translation с пустым name (min_length=1),
// поэтому при submit фильтруем translations и отправляем только заполненные.
const schema = z.object({
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/, "Только латиница, цифры и дефис"),
  parent_id: z.number().nullable(),
  sort_order: z.number().int(),
  is_visible: z.boolean(),
  translations: z.object({
    ru: z.object({
      name: z.string().min(1, "Название обязательно"),
      description: z.string().nullable().optional(),
    }),
    uz: z.object({
      name: z.string().optional(),
      description: z.string().nullable().optional(),
    }),
    en: z.object({
      name: z.string().optional(),
      description: z.string().nullable().optional(),
    }),
  }),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  mode: "create" | "edit";
  categoryId?: number;
  onSaved?: () => void;
}

export function CategoryEditor({ mode, categoryId, onSaved }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: category } = useQuery({
    queryKey: ["admin-category", categoryId],
    queryFn: async () => {
      if (!categoryId) return null;
      const { data } = await api.get(`/categories/${categoryId}`);
      return data;
    },
    enabled: mode === "edit" && Boolean(categoryId),
  });

  const { register, handleSubmit, reset, setValue, watch, formState } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        slug: "",
        parent_id: null,
        sort_order: 0,
        is_visible: true,
        translations: {
          ru: { name: "", description: null },
          uz: { name: "", description: null },
          en: { name: "", description: null },
        },
      },
    });

  // Заполняем форму при загрузке категории
  useEffect(() => {
    if (category) {
      const translationsMap: any = {};
      for (const tr of category.translations ?? []) {
        translationsMap[tr.language] = {
          name: tr.name,
          description: tr.description,
        };
      }
      for (const lang of langs) {
        if (!translationsMap[lang]) {
          translationsMap[lang] = { name: "", description: null };
        }
      }
      reset({
        slug: category.slug,
        parent_id: category.parent_id,
        sort_order: category.sort_order,
        is_visible: category.is_visible,
        translations: translationsMap,
      });
    }
  }, [category, reset]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const { translations, ...base } = values;
      // Фильтруем translations: backend reject'ит пустые names (min_length=1).
      // Юзер обязан заполнить ru; uz/en — опционально.
      const trArray = Object.entries(translations)
        .filter(
          ([, data]) =>
            data && typeof data.name === "string" && data.name.trim() !== "",
        )
        .map(([language, data]) => ({
          language,
          ...data,
        }));

      if (mode === "create") {
        const { data } = await api.post("/categories", {
          ...base,
          translations: trArray,
        });
        return data;
      } else {
        // update основных полей
        await api.patch(`/categories/${categoryId}`, base);
        // upsert translations по одному
        for (const tr of trArray) {
          await api.post(`/categories/${categoryId}/translations`, tr);
        }
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Категория создана" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["admin-category", categoryId] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-raw"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-flat"] });
      onSaved?.();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Ошибка");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!categoryId) return;
      await api.delete(`/categories/${categoryId}`);
    },
    onSuccess: () => {
      toast.success("Категория удалена");
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-raw"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-flat"] });
      onSaved?.();
    },
    onError: (e: any) => {
      const msg =
        e?.response?.data?.error?.message ??
        e?.response?.data?.detail ??
        "Не удалось удалить категорию";
      toast.error(msg);
    },
  });

  const isVisible = watch("is_visible");

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => deleteMutation.mutateAsync(),
    title: "Удалить категорию?",
    description:
      "Товары останутся, но будут отвязаны от этой категории.",
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {mode === "create"
            ? t("common.create") + " категории"
            : "Редактирование категории"}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <form
          onSubmit={handleSubmit((values) => saveMutation.mutate(values))}
          className="space-y-4"
        >
          <Tabs defaultValue="general">
            <TabsList>
              <TabsTrigger value="general">Основное</TabsTrigger>
              <TabsTrigger value="icon">Иконка</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6 pt-4">
              {/* 1. Название и описание (RU) — с этого начинаем заполнение */}
              <div className="space-y-3 p-4 border rounded-md">
                <Label className="font-semibold">1. Название и описание</Label>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Название *</Label>
                  <Input
                    placeholder="Например: Холодильники"
                    {...register("translations.ru.name")}
                  />
                  {formState.errors.translations?.ru?.name && (
                    <p className="text-sm text-destructive">
                      {formState.errors.translations.ru.name.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Описание</Label>
                  <Textarea
                    placeholder="Описание (опционально)"
                    {...register("translations.ru.description")}
                  />
                </div>
              </div>

              {/* 2. Перевод на другие языки */}
              <div className="space-y-3">
                <Label className="font-semibold">2. Перевод на другие языки</Label>
                {langs
                  .filter((lang) => lang !== "ru")
                  .map((lang) => (
                    <div key={lang} className="space-y-2 p-4 border rounded-md">
                      <div className="flex items-center justify-between">
                        <Label className="font-semibold uppercase">{lang}</Label>
                        <TranslateButton
                          sourceText={watch("translations.ru.name") ?? ""}
                          targetLang={lang as "uz" | "en"}
                          onTranslated={(text) =>
                            setValue(`translations.${lang}.name`, text, {
                              shouldDirty: true,
                            })
                          }
                        />
                      </div>
                      <Input
                        placeholder="Название"
                        {...register(`translations.${lang}.name`)}
                      />
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">
                          Описание
                        </Label>
                        <TranslateButton
                          sourceText={watch("translations.ru.description") ?? ""}
                          targetLang={lang as "uz" | "en"}
                          onTranslated={(text) =>
                            setValue(`translations.${lang}.description`, text, {
                              shouldDirty: true,
                            })
                          }
                        />
                      </div>
                      <Textarea
                        placeholder="Описание (опционально)"
                        {...register(`translations.${lang}.description`)}
                      />
                    </div>
                  ))}
              </div>

              {/* 3. Параметры категории */}
              <div className="space-y-3">
                <Label className="font-semibold">3. Параметры категории</Label>
                <div className="space-y-2">
                  <Label>Родительская категория</Label>
                  <ParentCategoryPicker
                    value={watch("parent_id")}
                    onChange={(id) =>
                      setValue("parent_id", id, { shouldDirty: true })
                    }
                    excludeId={categoryId}
                  />
                  <p className="text-xs text-muted-foreground">
                    «Без родителя» — категория станет корневой и появится
                    на главной странице сайта.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input {...register("slug")} placeholder="keramicheskaya-plitka" />
                  {formState.errors.slug && (
                    <p className="text-sm text-destructive">
                      {formState.errors.slug.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Порядок сортировки</Label>
                  <Input
                    type="number"
                    {...register("sort_order", { valueAsNumber: true })}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={isVisible}
                    onCheckedChange={(v) => setValue("is_visible", v)}
                  />
                  <Label>Видимая</Label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="icon" className="pt-4">
              <CategoryIconUploader
                categoryId={categoryId}
                iconUrl={category?.icon_url ?? null}
              />
            </TabsContent>
          </Tabs>

          <div className="flex items-center justify-start gap-2 pt-4 border-t">
            {mode === "edit" && categoryId && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => askDelete(true)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                {deleteMutation.isPending ? "Удаление…" : t("common.delete")}
              </Button>
            )}
          </div>

          <StickySaveBar
            dirty={formState.isDirty}
            saving={saveMutation.isPending}
            onDiscard={() => reset()}
          />
        </form>
        {deleteDialog}
      </CardContent>
    </Card>
  );
}
